import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import Footer from '../../components/Footer';
import TabBar from '../../components/Common/TabBar';
import { useAuth } from '../../contexts/AuthContext';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getHeaderTextSizeClass, getStatisticLabelSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';
import { PERFORMANCE_STATISTICS, CATEGORY_HEADERS } from '../../utils/performanceTabUtils';
import { formatPerformanceStatisticValue, getPerformanceStatisticColor } from '../../hooks/usePerformanceTabStatistics';
import { tickerApi } from '../../services/tickerApi';
import { userTickerApi } from '../../services/userTickerApi';
import { publicListApi } from '../../services/publicListApi';
import { useTickerPageMeta } from '../../hooks/useTickerPageMeta';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { loadUserTickerPreferences, saveUserTickerPreferences } from '../../utils/tickerPreferencesUtils';

type PerformanceHistoryRow = {
  tickerSymbol: string;
  tickerBasicsId: number;
  date: string | Date | null;
  ytdReturn: number | null;
  oneMReturn: number | null;
  threeMReturn: number | null;
  sixMReturn: number | null;
  nineMReturn: number | null;
  oneYReturn: number | null;
  twoYReturn: number | null;
  threeYReturn: number | null;
  fourYReturn: number | null;
  fiveYReturn: number | null;
};

const TickerPerformance: React.FC = () => {
  const { ticker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { fontSize } = useFontSize();

  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const isPublicList = !!publicUuid;

  // Full screen state (match TickerTechnicals behavior)
  const [isFullScreen, setIsFullScreen] = useState(false);
  useBodyScrollLock(isFullScreen);

  // Close full screen on Escape (match other Ticker* pages)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullScreen]);

  const [tickerName, setTickerName] = useState<string>(() => {
    const cachedName = sessionStorage.getItem(`ticker-name-${ticker}`);
    return cachedName || '';
  });
  const [tickerSymbol, setTickerSymbol] = useState<string>(() => {
    const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${ticker}`);
    return cachedSymbol ?? ticker ?? '';
  });
  const [tickerBasicsId, setTickerBasicsId] = useState<number | null>(null);

  const [history, setHistory] = useState<PerformanceHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Edit-mode state (match TickerTechnicals UX, adapted for performance date columns)
  const [isEditingColumns, setIsEditingColumns] = useState(false);
  const [hiddenStatistics, setHiddenStatistics] = useState<string[]>(() => {
    const saved = sessionStorage.getItem(`stocktable-ticker-performance-hidden-statistics-${ticker}`);
    return saved ? JSON.parse(saved) : [];
  });
  const [tempHiddenStatistics, setTempHiddenStatistics] = useState<string[]>([]);

  // Polling state (match ticker pages)
  const [isSyncing, setIsSyncing] = useState(false);
  const [pollingLimitReached, setPollingLimitReached] = useState(false);
  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingSyncRef = useRef(false);
  const currentAttemptRef = useRef(1);

  // Public list meta (owner pro status isn't needed for performance, but keep parity with other pages)
  const [publicListData, setPublicListData] = useState<{ ownerHasProSubscription: boolean } | null>(null);

  useTickerPageMeta({
    tickerSymbol,
    tickerName,
    pageType: 'performance',
  } as any);

  useEffect(() => {
    if (publicUuid && !publicListData) {
      publicListApi
        .getPublicList(publicUuid)
        .then((response) => {
          setPublicListData({ ownerHasProSubscription: response.data.owner_has_pro_subscription });
        })
        .catch(() => {
          // ignore
        });
    }
  }, [publicUuid, publicListData]);

  const api = useMemo(() => (isAuthenticated ? userTickerApi : tickerApi), [isAuthenticated]);

  // When logged in (and not viewing a public list), load saved preferences from server.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (isPublicList) return;
    if (!tickerBasicsId) return;

    loadUserTickerPreferences(tickerBasicsId, 'performance')
      .then(({ hiddenStatistics: hiddenFromServer }) => {
        setHiddenStatistics(hiddenFromServer || []);
        try {
          sessionStorage.setItem(
            `stocktable-ticker-performance-hidden-statistics-${ticker}`,
            JSON.stringify(hiddenFromServer || []),
          );
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        // If none exists, fall back to sessionStorage initialization.
      });
  }, [isAuthenticated, isPublicList, tickerBasicsId, ticker]);

  const clearPollingInterval = () => {
    if (syncPollingIntervalRef.current) {
      clearInterval(syncPollingIntervalRef.current);
      syncPollingIntervalRef.current = null;
    }
  };

  const stopPolling = () => {
    pollingStoppedRef.current = true;
    clearPollingInterval();
  };

  const fetchPerformanceHistory = async (basicsId: number) => {
    const resp = await api.getTickerPerformanceHistory({ tickerBasicsId: basicsId, limit: 6 });
    const rows: PerformanceHistoryRow[] = resp?.data || [];
    setHistory(rows);

    // lastUpdated from latest record date (best effort)
    const dates = rows
      .map((r) => (r.date ? new Date(r.date as any) : null))
      .filter((d): d is Date => !!d && !isNaN(d.getTime()));
    if (dates.length > 0) {
      setLastUpdated(new Date(Math.max(...dates.map((d) => d.getTime()))));
    }
  };

  const checkSyncAndFetch = async (attemptNumber: number = 1) => {
    if (!ticker) return;
    if (pollingStoppedRef.current) return;
    if (isCheckingSyncRef.current) return;
    isCheckingSyncRef.current = true;
    try {
      const symbol = String(ticker).toUpperCase();
      const syncResponse = await api.checkTickerSyncStatus({ ticker_symbols: [symbol] });
      const status = syncResponse?.data?.tickers?.[0];
      if (!status) {
        setError('Invalid sync status response');
        stopPolling();
        return;
      }

      if (status.ticker_name) {
        setTickerName(status.ticker_name);
        sessionStorage.setItem(`ticker-name-${symbol}`, status.ticker_name);
      }
      if (status.ticker_symbol) {
        setTickerSymbol(status.ticker_symbol);
        sessionStorage.setItem(`ticker-symbol-${symbol}`, status.ticker_symbol);
      }

      if (status.is_synced) {
        setIsSyncing(false);
        stopPolling();
        setPollingLimitReached(false);
        setTickerBasicsId(status.ticker_basics_id);
        await fetchPerformanceHistory(status.ticker_basics_id);
        return;
      }

      // Not yet synced → poll
      setIsSyncing(true);
      if (attemptNumber >= 20) {
        setPollingLimitReached(true);
        stopPolling();
        return;
      }

      clearPollingInterval();
      pollingStoppedRef.current = false;
      currentAttemptRef.current = attemptNumber;
      syncPollingIntervalRef.current = setInterval(() => {
        if (pollingStoppedRef.current) {
          clearPollingInterval();
          return;
        }
        const nextAttempt = currentAttemptRef.current + 1;
        currentAttemptRef.current = nextAttempt;
        checkSyncAndFetch(nextAttempt);
      }, 2000);
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response?.status === 429) {
        setError('Rate limit exceeded. Please try again later.');
      } else {
        setError(err?.message || 'Failed to load performance data');
      }
      stopPolling();
    } finally {
      isCheckingSyncRef.current = false;
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    setHistory([]);
    setPollingLimitReached(false);
    pollingStoppedRef.current = false;
    clearPollingInterval();

    checkSyncAndFetch(1).finally(() => setLoading(false));
    return () => {
      clearPollingInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, isAuthenticated, publicUuid]);

  // Records newest-first; display columns left→right latest→earliest (match request + other ticker pages)
  const columns = useMemo(() => {
    const rows = [...history];
    rows.sort((a, b) => {
      const ad = a.date ? new Date(a.date as any).getTime() : 0;
      const bd = b.date ? new Date(b.date as any).getTime() : 0;
      return bd - ad;
    });
    return rows;
  }, [history]);

  // Edit mode in TickerPerformance only supports selecting/deselecting indicator rows.
  // Columns (dates) are always shown and are not selectable.
  const visibleColumns = columns;

  const visibleStatistics = useMemo(() => {
    if (isEditingColumns) return PERFORMANCE_STATISTICS;
    return PERFORMANCE_STATISTICS.filter((s) => !hiddenStatistics.includes(s.key));
  }, [hiddenStatistics, isEditingColumns]);

  const currentHiddenStatistics = isEditingColumns ? tempHiddenStatistics : hiddenStatistics;

  const handleStartEdit = () => {
    setTempHiddenStatistics([...hiddenStatistics]);
    setIsEditingColumns(true);
  };

  const handleCancelEdit = () => {
    setTempHiddenStatistics([]);
    setIsEditingColumns(false);
  };

  const handleDoneEdit = async () => {
    setHiddenStatistics([...tempHiddenStatistics]);
    sessionStorage.setItem(`stocktable-ticker-performance-hidden-statistics-${ticker}`, JSON.stringify(tempHiddenStatistics));
    if (isAuthenticated && !isPublicList && tickerBasicsId) {
      try {
        await saveUserTickerPreferences(tickerBasicsId, 'performance', tempHiddenStatistics);
      } catch {
        // keep UI state; server save failure shouldn't block edit mode
      }
    }
    setTempHiddenStatistics([]);
    setIsEditingColumns(false);
  };

  const toggleStatisticHidden = (statKey: string) => {
    if (!isEditingColumns) return;
    setTempHiddenStatistics((prev) => (prev.includes(statKey) ? prev.filter((k) => k !== statKey) : [...prev, statKey]));
  };

  const renderValue = (statKey: string, col: PerformanceHistoryRow) => {
    const symbol = col.tickerSymbol || tickerSymbol || (ticker || '');
    const tickerObj: any = { ticker: symbol };

    // Adapt row to the shape used by ListPerformance hook
    const performance: any = {
      ticker_basics_id: col.tickerBasicsId,
      date: col.date ? new Date(col.date as any).toISOString().slice(0, 10) : null,
      ytd_return: col.ytdReturn ?? null,
      one_m_return: col.oneMReturn ?? null,
      three_m_return: col.threeMReturn ?? null,
      six_m_return: col.sixMReturn ?? null,
      nine_m_return: col.nineMReturn ?? null,
      one_y_return: col.oneYReturn ?? null,
      two_y_return: col.twoYReturn ?? null,
      three_y_return: col.threeYReturn ?? null,
      four_y_return: col.fourYReturn ?? null,
      five_y_return: col.fiveYReturn ?? null,
    };

    // Use the same formatter/coloring as ListPerformance
    const fakeData = { [symbol]: { performance } };
    return formatPerformanceStatisticValue(statKey, tickerObj, {
      tickerPerformanceData: fakeData,
      syncingTickers: isSyncing ? new Set([symbol]) : new Set(),
      unsyncedTickers: new Set(),
      pollingLimitReached: pollingLimitReached ? new Set([symbol]) : new Set(),
    } as any);
  };

  const headerDate = (d: string | Date | null) => {
    if (!d) return '-';
    const date = new Date(d as any);
    if (isNaN(date.getTime())) return String(d);
    // Match other Ticker* pages date notation (e.g. "APR 20, 2026")
    return date
      .toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
      .toUpperCase();
  };

  if (!ticker) return null;

  const handleBackClick = () => {
    if (publicUuid) navigate(`/performance?public_uuid=${publicUuid}`);
    else navigate('/performance');
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      {!isFullScreen && <Header sticky={false} />}
      {!isFullScreen && isPublicList && !isAuthenticated && <PublicListPromoBar />}

      <main className="px-1 lg:px-2 xl:px-4 pt-4 sm:pt-8 pb-4 sm:pb-8">
        <div className="bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md shadow-lg">
          {/* Back button */}
          <div className="p-4">
            <button
              onClick={handleBackClick}
              className="flex items-center text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors"
              type="button"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to List
            </button>
          </div>

          {/* Title */}
          <div className="px-4 pb-4">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold text-text-light-primary dark:text-text-dark-primary">
                {tickerName ? `${tickerName} (${tickerSymbol})` : tickerSymbol}
              </h1>
              {!isPublicList && (!isEditingColumns ? (
                <button
                  onClick={handleStartEdit}
                  className="transition-colors duration-200 text-gray-600 dark:text-text-secondary hover:text-gold-text-light dark:hover:text-highlight-gold"
                  title="Edit columns"
                  type="button"
                >
                  <svg
                    className="lucide lucide-pencil"
                    fill="none"
                    height="20"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    width="20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                    <path d="m15 5 4 4"></path>
                  </svg>
                </button>
              ) : (
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleCancelEdit}
                    className="text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors duration-200 text-sm font-medium"
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDoneEdit}
                    className="bg-primary text-gray-900 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors duration-200"
                    type="button"
                  >
                    Done
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-border-color">
            <nav className="px-4">
              <TabBar activeTab="performance" disabled={false} publicUuid={publicUuid} ticker={tickerSymbol} />
            </nav>
          </div>

          {error && (
            <div className="p-4 text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="w-full overflow-x-auto overflow-y-auto rounded-b-md" style={{ maxHeight: isFullScreen ? '85vh' : '80vh', position: 'relative' }}>
            <table className="min-w-full font-mono" style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
              <thead className="bg-gray-50 dark:bg-background-card sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                <tr>
                  <th
                    className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                    style={{ position: 'sticky', left: 0, zIndex: 40 }}
                  >
                    <div className="flex items-center">
                      <span>INDICATORS</span>
                    </div>
                  </th>
                  {visibleColumns.map((c, idx) => {
                    return (
                    <th
                      key={String(c.date ?? '')}
                        className={`py-1 px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                    >
                      <div className="flex items-center gap-2">
                        <span>{headerDate(c.date)}</span>
                      </div>
                    </th>
                    );
                  })}

                  {/* Full screen button column (sticky right) */}
                  <th
                    className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                    style={{ position: 'sticky', right: 0, zIndex: 40 }}
                  >
                    {!isEditingColumns && (
                      <button
                        onClick={() => setIsFullScreen(true)}
                        className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                        title="Open in full screen"
                        type="button"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                      </button>
                    )}
                  </th>
                </tr>
              </thead>

              <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                {/* Category header row (single category) */}
                {CATEGORY_HEADERS.map((cat) => (
                  <tr key={cat.label} className="bg-gray-100 dark:bg-border-color">
                    <td
                      className="py-1 sm:py-2 px-4 text-gray-900 dark:text-text-primary font-semibold text-sm bg-gray-100 dark:bg-border-color"
                      style={{ position: 'sticky', left: 0, zIndex: 30 }}
                    >
                      <div className="flex items-center gap-2">
                        <span>{cat.label}</span>
                      </div>
                    </td>
                    {visibleColumns.map((c) => (
                      <td
                        key={`${cat.label}-${String(c.date ?? '')}`}
                        className="px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-sm font-semibold text-gray-700 dark:text-text-primary border-b border-gray-200 dark:border-border-color bg-gray-100 dark:bg-border-color"
                      />
                    ))}

                    {/* Empty cell for full screen button column */}
                    <td className="px-2 sm:px-4 py-1 sm:py-2 text-sm font-semibold text-gray-700 dark:text-text-primary border-b border-gray-200 dark:border-border-color bg-gray-100 dark:bg-border-color" />
                  </tr>
                ))}

                {visibleStatistics.map((stat) => (
                  <tr
                    key={stat.key}
                    className={`h-12 sm:h-16 ${isEditingColumns && currentHiddenStatistics.includes(stat.key) ? 'opacity-50' : ''}`}
                  >
                    <td
                      className="px-4 text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                      style={{ position: 'sticky', left: 0, zIndex: 30 }}
                    >
                      <div className="flex items-center gap-2">
                        {isEditingColumns && (
                          <button
                            onClick={() => toggleStatisticHidden(stat.key)}
                            className={`transition-all duration-200 p-1 rounded-full active:scale-90 ${
                              currentHiddenStatistics.includes(stat.key)
                                ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10'
                                : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                            }`}
                            title={currentHiddenStatistics.includes(stat.key) ? 'Show row' : 'Hide row'}
                            type="button"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {currentHiddenStatistics.includes(stat.key) ? (
                                <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                              ) : (
                                <>
                                  <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                  <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </>
                              )}
                            </svg>
                          </button>
                        )}
                        <span className={getStatisticLabelSizeClass(fontSize)}>{stat.label}</span>
                      </div>
                    </td>
                    {visibleColumns.map((c, idx) => {
                      const symbol = c.tickerSymbol || tickerSymbol;
                      const tickerObj: any = { ticker: symbol };
                      const color = getPerformanceStatisticColor(stat.key, tickerObj, {
                        tickerPerformanceData: {
                          [symbol]: {
                            performance: {
                              ticker_basics_id: c.tickerBasicsId,
                              date: c.date ? new Date(c.date as any).toISOString().slice(0, 10) : null,
                              ytd_return: c.ytdReturn ?? null,
                              one_m_return: c.oneMReturn ?? null,
                              three_m_return: c.threeMReturn ?? null,
                              six_m_return: c.sixMReturn ?? null,
                              nine_m_return: c.nineMReturn ?? null,
                              one_y_return: c.oneYReturn ?? null,
                              two_y_return: c.twoYReturn ?? null,
                              three_y_return: c.threeYReturn ?? null,
                              four_y_return: c.fourYReturn ?? null,
                              five_y_return: c.fiveYReturn ?? null,
                            },
                          },
                        },
                      } as any);

                      return (
                        <td
                          key={`${stat.key}-${String(c.date ?? '')}`}
                          className={`px-1 sm:px-2 md:px-4 lg:px-6 font-semibold ${getTextSizeClass(fontSize)} h-12 sm:h-16 bg-surface-light dark:bg-background-card ${color} ${
                            ''
                          }`}
                          style={{ paddingTop: '0px', paddingBottom: '0px' }}
                        >
                          {renderValue(stat.key, c)}
                        </td>
                      );
                    })}

                    {/* Empty cell for full screen button column */}
                    <td className="px-2 sm:px-4 h-12 sm:h-16 bg-surface-light dark:bg-background-card" />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </main>

      {/* Full Screen Overlay (match other Ticker* pages) */}
      {isFullScreen && (
        <div
          className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
          role="dialog"
          aria-modal="true"
        >
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain">
            <table className="min-w-full font-mono" style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
              <thead className="sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                <tr>
                  <th
                    className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                    style={{ position: 'sticky', left: 0, zIndex: 40 }}
                  >
                    <div className="flex items-center">
                      <span>INDICATORS</span>
                    </div>
                  </th>

                  {visibleColumns.map((c) => (
                    <th
                      key={String(c.date ?? '')}
                      className={`py-1 px-2 sm:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                    >
                      <div className="flex items-center">
                        <span>{headerDate(c.date)}</span>
                      </div>
                    </th>
                  ))}

                  {/* Close button - sticky right */}
                  <th
                    className="py-1 px-4 text-right bg-gray-50 dark:bg-background-card"
                    style={{ position: 'sticky', right: 0, zIndex: 40 }}
                  >
                    <button
                      onClick={() => setIsFullScreen(false)}
                      className="p-1.5 text-gray-500 dark:text-white hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                      title="Close full screen"
                      type="button"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </th>
                </tr>
              </thead>

              <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                {CATEGORY_HEADERS.map((cat) => (
                  <tr key={cat.label} className="bg-gray-100 dark:bg-border-color">
                    <td
                      className="py-1 sm:py-2 px-4 text-gray-900 dark:text-text-primary font-semibold text-sm bg-gray-100 dark:bg-border-color"
                      style={{ position: 'sticky', left: 0, zIndex: 30 }}
                    >
                      <div className="flex items-center gap-2">
                        <span>{cat.label}</span>
                      </div>
                    </td>
                    {visibleColumns.map((c) => (
                      <td
                        key={`${cat.label}-fs-${String(c.date ?? '')}`}
                        className="px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-sm font-semibold text-gray-700 dark:text-text-primary border-b border-gray-200 dark:border-border-color bg-gray-100 dark:bg-border-color"
                      />
                    ))}
                    <td className="px-4 py-1 sm:py-2 text-sm font-semibold text-gray-700 dark:text-text-primary border-b border-gray-200 dark:border-border-color bg-gray-100 dark:bg-border-color" />
                  </tr>
                ))}

                {visibleStatistics.map((stat) => (
                  <tr key={`fs-${stat.key}`} className="h-12 sm:h-16">
                    <td
                      className="px-4 text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                      style={{ position: 'sticky', left: 0, zIndex: 30 }}
                    >
                      <span className={getStatisticLabelSizeClass(fontSize)}>{stat.label}</span>
                    </td>
                    {visibleColumns.map((c, idx) => {
                      const symbol = c.tickerSymbol || tickerSymbol;
                      const tickerObj: any = { ticker: symbol };
                      const color = getPerformanceStatisticColor(stat.key, tickerObj, {
                        tickerPerformanceData: {
                          [symbol]: {
                            performance: {
                              ticker_basics_id: c.tickerBasicsId,
                              date: c.date ? new Date(c.date as any).toISOString().slice(0, 10) : null,
                              ytd_return: c.ytdReturn ?? null,
                              one_m_return: c.oneMReturn ?? null,
                              three_m_return: c.threeMReturn ?? null,
                              six_m_return: c.sixMReturn ?? null,
                              nine_m_return: c.nineMReturn ?? null,
                              one_y_return: c.oneYReturn ?? null,
                              two_y_return: c.twoYReturn ?? null,
                              three_y_return: c.threeYReturn ?? null,
                              four_y_return: c.fourYReturn ?? null,
                              five_y_return: c.fiveYReturn ?? null,
                            },
                          },
                        },
                      } as any);

                      return (
                        <td
                          key={`fs-${stat.key}-${String(c.date ?? '')}`}
                          className={`px-1 sm:px-2 md:px-4 lg:px-6 font-semibold ${getTextSizeClass(fontSize)} h-12 sm:h-16 bg-surface-light dark:bg-background-card ${color} ${
                            ''
                          }`}
                          style={{ paddingTop: '0px', paddingBottom: '0px' }}
                        >
                          {renderValue(stat.key, c)}
                        </td>
                      );
                    })}
                    <td className="px-4 h-12 sm:h-16 bg-surface-light dark:bg-background-card" />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!isFullScreen && (
        <div className="mt-4 sm:mt-8">
          <Footer />
        </div>
      )}
    </div>
  );
};

export default TickerPerformance;

